import { useState } from 'react';
import { Link } from '@tanstack/react-router';
import { AlertTriangle, ArrowLeft, Eye } from 'lucide-react';
import { Button } from '../../components/Button';
import { Card, CardHeader } from '../../components/Card';
import { Banner } from '../../components/Banner';
import { Input } from '../../components/Input';

/** Migrate subscribers between plans — orchestration form. */
export function MigrateSubscribers() {
  const [cutover, setCutover] = useState<'rolling' | 'bulk'>('rolling');
  const [pricing, setPricing] = useState<'difference' | 'lock'>('difference');
  const [notice, setNotice] = useState<'60' | '30' | '14' | 'now'>('60');
  const [emailSubs, setEmailSubs] = useState(true);
  const [emailResellers, setEmailResellers] = useState(true);
  const [showBanner, setShowBanner] = useState(true);
  const [priceMatch, setPriceMatch] = useState<'auto' | 'specific'>('auto');

  return (
    <div className="px-4 sm:px-6 lg:px-8 py-6 sm:py-8 max-w-3xl mx-auto">
      <Link to="/admin/packages" className="inline-flex items-center gap-1.5 text-sm text-zinc-500 dark:text-zinc-400 hover:text-zinc-900 dark:hover:text-zinc-100 mb-4 transition">
        <ArrowLeft className="w-4 h-4" />
        All packages
      </Link>

      <div className="mb-6">
        <p className="text-xs font-medium uppercase tracking-wider text-zinc-500 dark:text-zinc-400 mb-1.5">Pricing</p>
        <h1 className="text-2xl sm:text-3xl font-semibold tracking-tight text-zinc-950 dark:text-zinc-50">
          Migrate subscribers between plans
        </h1>
        <p className="mt-1 text-sm text-zinc-600 dark:text-zinc-300">
          Carefully move subscribers from one plan to another with audit trail and notice.
        </p>
      </div>

      <div className="space-y-6">
        <Card className="p-5 sm:p-6">
          <CardHeader title="Source &amp; target" />
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-zinc-900 dark:text-zinc-100 mb-1.5">From package <span className="text-rose-600">*</span></label>
              <select className="block w-full px-3 py-2.5 text-sm rounded-md bg-white dark:bg-zinc-900 border border-zinc-300 dark:border-zinc-700">
                <option>Pro v1 (deprecated) — 31 active subscribers · BDT 1,200/mo · BDT 12,000/yr</option>
                <option>LTD-2025 (deprecated) — 42 subscribers · one-time</option>
              </select>
              <p className="mt-1.5 text-xs text-zinc-500 dark:text-zinc-400">31 active subscribers · BDT 1,200/mo · BDT 12,000/yr</p>
            </div>

            <div>
              <label className="block text-sm font-medium text-zinc-900 dark:text-zinc-100 mb-1.5">To package <span className="text-rose-600">*</span></label>
              <select className="block w-full px-3 py-2.5 text-sm rounded-md bg-white dark:bg-zinc-900 border border-zinc-300 dark:border-zinc-700">
                <option>Pro (current)</option>
                <option>Pro 2026 v2 (draft)</option>
              </select>
            </div>

            <div>
              <span className="block text-sm font-medium text-zinc-900 dark:text-zinc-100 mb-2">To price <span className="text-rose-600">*</span></span>
              <div className="space-y-2">
                <Radio name="price-match" checked={priceMatch === 'auto'} onChange={() => setPriceMatch('auto')} label="Match interval + currency (auto)" />
                <Radio name="price-match" checked={priceMatch === 'specific'} onChange={() => setPriceMatch('specific')} label="Specific price" />
              </div>
              {priceMatch === 'specific' && (
                <select className="mt-2 block w-full px-3 py-2.5 text-sm rounded-md bg-white dark:bg-zinc-900 border border-zinc-300 dark:border-zinc-700">
                  <option>BDT Monthly ৳1,500</option>
                  <option>BDT Yearly ৳15,000</option>
                </select>
              )}
            </div>
          </div>
        </Card>

        <Card className="p-5 sm:p-6">
          <CardHeader title="Cutover" subtitle="When does each subscriber actually move?" />
          <div className="space-y-2">
            <Radio name="cut" checked={cutover === 'rolling'} onChange={() => setCutover('rolling')} label="At each subscriber's next renewal (rolling)" />
            <Radio name="cut" checked={cutover === 'bulk'} onChange={() => setCutover('bulk')} label="Single bulk cutover" />
          </div>
          {cutover === 'bulk' && (
            <div className="mt-3 max-w-xs">
              <Input label="Cutover date" type="date" defaultValue="2026-07-01" />
            </div>
          )}
        </Card>

        <Card className="p-5 sm:p-6">
          <CardHeader title="Pricing during cutover" />
          <div className="space-y-2">
            <Radio
              name="pricing"
              checked={pricing === 'difference'}
              onChange={() => setPricing('difference')}
              label="Price difference applies — they'll pay new price next cycle"
            />
            <Radio
              name="pricing"
              checked={pricing === 'lock'}
              onChange={() => setPricing('lock')}
              label="Lock current price as a custom-price grandfather (extra discount)"
            />
          </div>
        </Card>

        <Card className="p-5 sm:p-6">
          <CardHeader title="Notice" subtitle="Industry standard is 60 days for migrations" />
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
            {[
              { v: '60', l: '60 days', tone: 'good' },
              { v: '30', l: '30 days', tone: 'normal' },
              { v: '14', l: '14 days', tone: 'normal' },
              { v: 'now', l: 'Now', tone: 'risk' },
            ].map((n) => (
              <label
                key={n.v}
                className={`flex items-center justify-center gap-2 text-sm p-2.5 rounded-md border-2 cursor-pointer transition ${
                  notice === n.v
                    ? 'border-brand bg-brand-subtle dark:border-brand-dm dark:bg-brand-dm-subtle/30 font-medium'
                    : 'border-zinc-200 dark:border-zinc-700 hover:border-zinc-300 dark:hover:border-zinc-600'
                }`}
              >
                <input type="radio" name="notice" checked={notice === n.v} onChange={() => setNotice(n.v as typeof notice)} className="text-brand focus:ring-brand" />
                <span>{n.l}</span>
              </label>
            ))}
          </div>

          <div className="mt-4 space-y-2">
            <label className="flex items-center gap-2 text-sm text-zinc-700 dark:text-zinc-200 cursor-pointer">
              <input type="checkbox" checked={emailSubs} onChange={(e) => setEmailSubs(e.target.checked)} className="rounded border-zinc-300 dark:border-zinc-600 text-brand focus:ring-brand" />
              Email subscribers
            </label>
            <label className="flex items-center gap-2 text-sm text-zinc-700 dark:text-zinc-200 cursor-pointer">
              <input type="checkbox" checked={emailResellers} onChange={(e) => setEmailResellers(e.target.checked)} className="rounded border-zinc-300 dark:border-zinc-600 text-brand focus:ring-brand" />
              Email reseller admins
            </label>
            <label className="flex items-center gap-2 text-sm text-zinc-700 dark:text-zinc-200 cursor-pointer">
              <input type="checkbox" checked={showBanner} onChange={(e) => setShowBanner(e.target.checked)} className="rounded border-zinc-300 dark:border-zinc-600 text-brand focus:ring-brand" />
              Show in-app banner
            </label>
          </div>
        </Card>

        <Card className="p-5 sm:p-6 bg-zinc-50/40 dark:bg-zinc-950/40">
          <CardHeader title="Audit log entry" />
          <p className="text-sm text-zinc-600 dark:text-zinc-300">
            Will record: <span className="mono text-xs">initiator, count, source/target, notice period, timestamp, IP</span>
          </p>
        </Card>

        <Banner tone="warning" title="This affects 31 subscribers across 3 resellers">
          <div className="flex items-center justify-between gap-3 flex-wrap mt-1">
            <span>Review the affected list before scheduling.</span>
            <Button variant="secondary" size="sm" iconLeft={<Eye className="w-3.5 h-3.5" />}>
              View affected (31)
            </Button>
          </div>
        </Banner>

        <div className="flex flex-col sm:flex-row sm:justify-between gap-3 pt-3">
          <Link to="/admin/packages">
            <Button variant="ghost">Cancel</Button>
          </Link>
          <Button>Schedule migration</Button>
        </div>
      </div>
    </div>
  );
}

function Radio({
  name,
  checked,
  onChange,
  label,
}: {
  name: string;
  checked: boolean;
  onChange: () => void;
  label: string;
}) {
  return (
    <label className="flex items-center gap-2.5 text-sm text-zinc-700 dark:text-zinc-200 cursor-pointer">
      <input type="radio" name={name} checked={checked} onChange={onChange} className="text-brand focus:ring-brand" />
      {label}
    </label>
  );
}
